// Athena Protein Water — Campaign Landing Page interactions

document.addEventListener('DOMContentLoaded', () => {

  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const scrollBehavior = prefersReduced ? 'auto' : 'smooth';

  const getScrollPadding = (track) => Number.parseFloat(getComputedStyle(track).scrollPaddingInlineStart) || 0;

  const getItemScrollPosition = (track, item) => {
    return item.offsetLeft - track.offsetLeft - getScrollPadding(track);
  };

  const getCarouselStep = (track, items) => {
    const item = items[0];
    if (!item) return 0;

    const gap = Number.parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
    return item.offsetWidth + gap;
  };

  const getActiveIndexFromScroll = (track, items) => {
    const scrollLeft = track.scrollLeft;
    let closestIndex = 0;
    let closestDistance = Infinity;

    for (let i = 0; i < items.length; i++) {
      const distance = Math.abs(getItemScrollPosition(track, items[i]) - scrollLeft);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = i;
      }
    }

    return closestIndex;
  };

  const updateCarouselDots = (wrap, activeIndex) => {
    for (const dot of wrap.querySelectorAll('[data-pw-carousel-dot]')) {
      const isActive = Number(dot.dataset.pwCarouselDot) === activeIndex;
      dot.setAttribute('aria-selected', String(isActive));
    }
  };

  const scrollToCarouselIndex = (track, items, index) => {
    const item = items[index];
    if (!item) return;

    track.scrollTo({ left: getItemScrollPosition(track, item), behavior: scrollBehavior });
  };

  for (const wrap of document.querySelectorAll('.pw-carousel-wrap')) {
    const track = wrap.querySelector('[data-pw-carousel-track]');
    if (!track) continue;

    const items = [...track.children].filter((child) => child.matches('.pw-tile, .pw-review-card'));
    if (items.length <= 1) continue;

    const prevBtn = wrap.querySelector('.pw-prev');
    const nextBtn = wrap.querySelector('.pw-next');
    const dots = [...wrap.querySelectorAll('[data-pw-carousel-dot]')];
    let scrollTimeout;

    const syncCarouselDots = () => {
      if (!dots.length) return;
      if (!window.matchMedia('(max-width: 640px)').matches && wrap.closest('.pw-reviews')) return;

      updateCarouselDots(wrap, getActiveIndexFromScroll(track, items));
    };

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        track.scrollBy({ left: -getCarouselStep(track, items), behavior: scrollBehavior });
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        track.scrollBy({ left: getCarouselStep(track, items), behavior: scrollBehavior });
      });
    }

    for (const dot of dots) {
      dot.addEventListener('click', () => {
        const index = Number(dot.dataset.pwCarouselDot);
        scrollToCarouselIndex(track, items, index);
        updateCarouselDots(wrap, index);
      });
    }

    track.addEventListener('scroll', () => {
      window.clearTimeout(scrollTimeout);
      scrollTimeout = window.setTimeout(syncCarouselDots, 80);
    }, { passive: true });

    syncCarouselDots();
  }

  // ── UGC video tiles — click to play / pause ──────────────────────────────
  for (const tile of document.querySelectorAll('.pw-ugc-tile')) {
    const video = tile.querySelector('video');
    const playBtn = tile.querySelector('.pw-ugc-play');
    if (!video) continue;

    const setPlaying = (playing) => {
      tile.classList.toggle('playing', playing);
      if (!playBtn) return;
      playBtn.setAttribute('aria-pressed', String(playing));
    };

    tile.addEventListener('click', () => {
      for (const other of document.querySelectorAll('.pw-ugc-tile')) {
        if (other !== tile) {
          other.classList.remove('playing');
          const otherBtn = other.querySelector('.pw-ugc-play');
          if (otherBtn) otherBtn.setAttribute('aria-pressed', 'false');
          other.querySelector('video')?.pause();
        }
      }

      if (video.paused) {
        if (!video.src && video.dataset.src) video.src = video.dataset.src;
        video.muted = false;
        video.play();
        setPlaying(true);
      } else {
        video.pause();
        setPlaying(false);
      }
    });
  }

  // ── Purchase type toggle (One-Time vs Subscribe & Save) ──────────────────
  const toggleBtns = document.querySelectorAll('.pw-purchase-toggle button');
  const rangeGrid = document.querySelector('.pw-range-grid');

  for (const btn of toggleBtns) {
    btn.addEventListener('click', () => {
      for (const b of toggleBtns) {
        b.classList.remove('active');
        b.setAttribute('aria-pressed', 'false');
      }
      btn.classList.add('active');
      btn.setAttribute('aria-pressed', 'true');

      if (rangeGrid) {
        rangeGrid.classList.toggle('subscribe-active', btn.dataset.mode === 'subscribe');
      }
    });
  }

  // ── Shop range stars — fetch rating directly from Reviews.io API ────────
  const reviewsStore = window.reviewsIoStore;

  if (reviewsStore) {
    const ratingCache = new Map();

    const fetchRating = async (sku) => {
      if (ratingCache.has(sku)) return ratingCache.get(sku);
      try {
        const res = await fetch(`https://api.reviews.io/product/rating?sku=${encodeURIComponent(sku)}&store=${encodeURIComponent(reviewsStore)}`);
        const data = res.ok ? await res.json() : null;
        ratingCache.set(sku, data);
        return data;
      } catch {
        return null;
      }
    };

    for (const wrap of document.querySelectorAll('.pw-stars-wrap')) {
      const dataSku = wrap.querySelector('.ruk_rating_snippet')?.dataset.sku;
      const metaEl = wrap.querySelector('.pw-stars-meta');
      if (!dataSku || !metaEl) continue;

      const skus = dataSku.split(';').slice(1).filter(p => p && isNaN(Number(p)));

      Promise.all(skus.map(sku => fetchRating(sku))).then((results) => {
        const valid = results.filter(d => d?.count > 0);
        const starsEl = wrap.querySelector('.pw-stars');

        if (!valid.length) {
          starsEl?.classList.add('pw-stars--no-reviews');
          metaEl.textContent = '(0 reviews)';
          return;
        }

        const totalCount = valid.reduce((sum, d) => sum + d.count, 0);
        const weightedSum = valid.reduce((sum, d) => sum + parseFloat(d.rating) * d.count, 0);
        const avgRating = (weightedSum / totalCount).toFixed(1);

        metaEl.textContent = `${avgRating} (${totalCount} reviews)`;
        starsEl?.removeAttribute('aria-hidden');
      });
    }
  }

  // ── Scroll-triggered entrance animations ─────────────────────────────────
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('pw-revealed');
            observer.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );

    for (const el of document.querySelectorAll('.pw-reveal')) {
      observer.observe(el);
    }
  } else {
    for (const el of document.querySelectorAll('.pw-reveal')) {
      el.classList.add('pw-revealed');
    }
  }
});
